"use client";

// # FILE: src/shared/providers/error-handler-provider.tsx
// # VERSION: 1.0.0
// # START_MODULE_CONTRACT:
// # PURPOSE: Провайдер для глобальной обработки ошибок и интеграции с UI.
// # SCOPE: Глобальная обработка ошибок приложения.
// # INPUT: Дочерние компоненты.
// # OUTPUT: Провайдер с обработкой ошибок.
// # KEYWORDS: [DOMAIN(9): ErrorHandling; TECH(8): React; PATTERN(7): Provider]
// # END_MODULE_CONTRACT

import React, { createContext, useContext, useCallback, ReactNode } from "react";
import { ErrorBoundary } from "@oxpa/shared/components/error-boundary";
import { Toaster } from "@oxpa/shared/ui/toaster";
import {
  useApiError,
  ApiError,
  ErrorHandlerOptions,
} from "@oxpa/shared/hooks/use-api-error";
import { PostgrestError } from "@supabase/supabase-js";
import * as Sentry from "@sentry/nextjs";

// START_CONTEXT_ErrorHandlerContext
// START_CONTRACT:
// PURPOSE: Контекст для обработчика ошибок.
// END_CONTRACT
interface ErrorHandlerContextType {
  handleError: (
    error: PostgrestError | Error | null | undefined,
    options?: ErrorHandlerOptions
  ) => ApiError | null;
  handleSuccess: (message: string, title?: string) => void;
  wrapAsync: <T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    options?: ErrorHandlerOptions
  ) => (...args: T) => Promise<R | null>;
}

const ErrorHandlerContext = createContext<ErrorHandlerContextType | null>(null);
// END_CONTEXT_ErrorHandlerContext

// START_HOOK_useErrorHandler
// START_CONTRACT:
// PURPOSE: Хук для доступа к глобальному обработчику ошибок.
// OUTPUTS:
// - ErrorHandlerContextType - Методы обработки ошибок
// END_CONTRACT
export function useErrorHandler(): ErrorHandlerContextType {
  const context = useContext(ErrorHandlerContext);
  if (!context) {
    throw new Error(
      "useErrorHandler должен использоваться внутри ErrorHandlerProvider"
    );
  }
  return context;
}
// END_HOOK_useErrorHandler

// START_INTERFACE_ErrorHandlerProviderProps
interface ErrorHandlerProviderProps {
  children: ReactNode;
  onGlobalError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}
// END_INTERFACE_ErrorHandlerProviderProps

// START_FUNCTION_ErrorHandlerProvider
// START_CONTRACT:
// PURPOSE: Провайдер для глобальной обработки ошибок.
// INPUTS:
// - [Дочерние компоненты] => children: ReactNode
// - [Обработчик глобальных ошибок] => onGlobalError: function
// END_CONTRACT
export function ErrorHandlerProvider({
  children,
  onGlobalError,
}: ErrorHandlerProviderProps) {
  const { handleError, handleSuccess, wrapAsync } = useApiError();

  // START_BLOCK_GLOBAL_ERROR_HANDLER
  const handleGlobalError = useCallback(
    (error: Error, errorInfo: React.ErrorInfo) => {
      console.error(
        "[ErrorHandlerProvider] Глобальная ошибка:",
        error,
        errorInfo
      );

      if (onGlobalError) {
        onGlobalError(error, errorInfo);
      }

      // Отправка ошибок в GlitchTip (Sentry-совместимый мониторинг)
      Sentry.captureException(error);
    },
    [onGlobalError]
  );
  // END_BLOCK_GLOBAL_ERROR_HANDLER

  // START_BLOCK_CONTEXT_VALUE
  const value: ErrorHandlerContextType = {
    handleError,
    handleSuccess,
    wrapAsync,
  };
  // END_BLOCK_CONTEXT_VALUE

  return (
    <ErrorHandlerContext.Provider value={value}>
      <ErrorBoundary onError={handleGlobalError}>
        {children}
        <Toaster />
      </ErrorBoundary>
    </ErrorHandlerContext.Provider>
  );
}
// END_FUNCTION_ErrorHandlerProvider
